import React, { useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import moment from "moment";
import { HiOutlineX } from "react-icons/hi";
import Button from "../../../../components/shared/Button/Button";
import Divider from "../../../../components/shared/Divider/Divider";
import Input from "../../../../components/shared/Input/Input";
import { useAdminSearchStore } from "../../store/adminSearchStore";

const formSchema = z.object({
  sessionName: z.string().optional(),
  sessionDateFrom: z.string().optional(),
  sessionDateTo: z.string().optional(),
});

export function SessionsFiltersPanel({ isOpen, onClose, onSubmit }) {
  const { sessionsState, setSessionsFilters } = useAdminSearchStore();
  const { filters } = sessionsState;

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isDirty },
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      sessionName: "",
      sessionDateFrom: "",
      sessionDateTo: "",
    },
    mode: "onChange",
  });

  const watchedValues = watch();

  useEffect(() => {
    if (isOpen) {
      reset({
        sessionName: filters.sessionName || "",
        sessionDateFrom: filters.sessionDate?.from
          ? moment(filters.sessionDate.from).format("YYYY-MM-DD")
          : "",
        sessionDateTo: filters.sessionDate?.to
          ? moment(filters.sessionDate.to).format("YYYY-MM-DD")
          : "",
      });
    }
  }, [filters, isOpen, reset]);

  const hasActiveFilters = useMemo(() => {
    return Object.values(watchedValues).some((v) => (v || "").trim() !== "");
  }, [watchedValues]);

  const handleClear = useCallback(() => {
    reset({
      sessionName: "",
      sessionDateFrom: "",
      sessionDateTo: "",
    });
    setSessionsFilters({
      sessionName: "",
      sessionDate: { from: null, to: null },
    });
    onSubmit?.();
  }, [reset, setSessionsFilters, onSubmit]);

  const handleApply = useCallback(
    (data) => {
      setSessionsFilters({
        sessionName: data.sessionName?.trim() || "",
        sessionDate: {
          from: data.sessionDateFrom
            ? moment(data.sessionDateFrom).startOf("day").valueOf()
            : null,
          to: data.sessionDateTo
            ? moment(data.sessionDateTo).endOf("day").valueOf()
            : null,
        },
      });
      onSubmit?.();
    },
    [setSessionsFilters, onSubmit]
  );

  if (!isOpen) return null;

  return (
    <div
      aria-label="Session Filters"
      aria-modal="true"
      className="font-manrope fixed right-0 top-0 z-[2000] flex h-screen w-full max-w-96 flex-col border-l border-gray200 bg-white p-6"
      role="dialog"
    >
      <div className="relative flex flex-col gap-1">
        <div className="text-2xl font-semibold">Filters</div>
        <div className="text-base text-gray600">
          Filter sessions by criteria
        </div>
        <button
          aria-label="Close"
          className="absolute right-0 top-0 w-fit rounded-sm p-2"
          onClick={onClose}
        >
          <HiOutlineX className="size-6 text-gray500" />
        </button>
      </div>

      <div className="mt-6 flex flex-1 flex-col gap-6 overflow-y-auto scrollbar-hide">
        <Input
          control={control}
          name="sessionName"
          label="Session Name"
          placeholder="Session name"
          size="md"
        />

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Input
            control={control}
            name="sessionDateFrom"
            label="Session Date From"
            type="date"
            size="md"
          />
          <Input
            control={control}
            name="sessionDateTo"
            label="Session Date To"
            type="date"
            size="md"
          />
        </div>
      </div>

      <div className="mt-auto">
        <Divider type="horizontal" color="gray200" />

        <div className="flex items-center justify-between pt-4">
          <button
            className={`text-lg font-semibold ${
              !hasActiveFilters ? "text-gray300" : "text-primary700"
            }`}
            onClick={handleClear}
          >
            Clear all
          </button>

          <div className="flex gap-2">
            <Button color="gray100" onClick={onClose} size="md">
              Cancel
            </Button>

            <Button
              onClick={handleSubmit(handleApply)}
              size="md"
              isDisabled={!hasActiveFilters && !isDirty}
            >
              Apply
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
